import React, { useState } from "react";
import { Send, CheckCircle } from "lucide-react";

const toAlignClass = (a) => {
  const v = String(a || "").toLowerCase().trim();
  if (v === "left") return "text-left";
  if (v === "right") return "text-right";
  if (v === "center") return "text-center";
  return "";
};

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 180;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const toDigits = (s) => String(s || "").replace(/\D/g, "");

const DEFAULTS = {
  title: "Let's Start a Conversation",
  subtitle: "Send us your details on WhatsApp and we will get back to you quickly.",

  nameLabel: "Full Name",
  namePlaceholder: "John Doe",

  phoneLabel: "Phone Number",
  phonePlaceholder: "9725...",

  messageLabel: "Message",
  messagePlaceholder: "Tell us what you need...",

  privacyText: "When you click send, your message will open in WhatsApp.",

  buttonText: "Send via WhatsApp",

  successTitle: "Opening WhatsApp...",
  successSubtitle: "Your message is ready to send.",
};

function buildWhatsAppText(cfg, formData) {
  return [
    `*${cfg.title || "New Contact Request"}*`,
    "",
    `Name: ${formData.name || "-"}`,
    `Phone: ${formData.phone || "-"}`,
    `Message: ${formData.message || "-"}`,
  ].join("\n");
}

export default function CTASection1({ config, style, businessPhone }) {
  const cfg = { ...DEFAULTS, ...(config || {}) };

  const [formData, setFormData] = useState({ name: "", phone: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  const targetPhone = toDigits(
    businessPhone ||
      config?.businessPhone ||
      cfg.businessPhone ||
      cfg.whatsappNumber ||
      ""
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!targetPhone) {
      alert("Business WhatsApp phone is missing. Please add it in General tab.");
      return;
    }

    if (!formData.name || !formData.phone || !formData.message) return;

    const text = encodeURIComponent(buildWhatsAppText(cfg, formData));
    setSubmitted(true);
    window.open(`https://wa.me/${targetPhone}?text=${text}`, "_blank");

    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: "", phone: "", message: "" });
    }, 1800);
  };

  const bg = buildBg(style, "bg-gray-50");

  const align = (style?.textAlign || "").trim();
  const hasAlign = align.length > 0;
  const headerAlignClass = hasAlign ? toAlignClass(align) : "text-center";
  const contentAlignClass = hasAlign ? toAlignClass(align) : "";

  const rawPrimary = cfg.buttonText;
  const showPrimary = rawPrimary === undefined ? true : String(rawPrimary).trim().length > 0;
  const primaryLabel = rawPrimary === undefined ? "Send via WhatsApp" : rawPrimary;

  const primaryHasCustom = Boolean(
    String(style?.primaryBtnBg || "").trim() || String(style?.primaryBtnText || "").trim()
  );

  const cardStyle = {
    backgroundColor: style?.ctaCardBg || undefined,
    borderColor: style?.ctaCardBorder || undefined,
    borderWidth: style?.ctaCardBorder ? "1px" : undefined,
  };

  const inputStyle = {
    backgroundColor: style?.ctaInputBg || undefined,
    borderColor: style?.ctaInputBorder || undefined,
    color: style?.ctaInputText || undefined,
  };

  const labelStyle = {
    color: style?.ctaInputLabelColor || undefined,
  };

  return (
    <section
      dir={config?.direction || "ltr"}
      style={bg.style}
      className={`py-14 px-4 ${bg.className}`}
    >
      <div className="container mx-auto max-w-3xl">
        <div
          style={cardStyle}
          className="rounded-2xl shadow-xl p-8 md:p-12 bg-white"
        >
          <div className={`${headerAlignClass} mb-10`}>
            <h2
              style={{ color: style?.titleColor || undefined }}
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
            >
              {cfg.title}
            </h2>

            <p
              style={{ color: style?.subtitleColor || undefined }}
              className="text-lg text-gray-600"
            >
              {cfg.subtitle}
            </p>
          </div>

          {submitted ? (
            <div className={`${contentAlignClass} py-12`}>
              <div
                style={{ backgroundColor: style?.ctaSuccessIconBg || undefined }}
                className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <CheckCircle
                  style={{ color: style?.ctaSuccessIconColor || undefined }}
                  className="w-10 h-10 text-green-600"
                />
              </div>
              <h3
                style={{ color: style?.ctaSuccessTitleColor || undefined }}
                className="text-2xl font-bold text-gray-900 mb-2"
              >
                {cfg.successTitle}
              </h3>
              <p
                style={{ color: style?.ctaSuccessSubtitleColor || style?.ctaMutedTextColor || undefined }}
                className="text-gray-600"
              >
                {cfg.successSubtitle}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className={`space-y-6 ${contentAlignClass}`}>
              <div>
                <label style={labelStyle} className="block text-sm font-medium text-gray-700 mb-2">
                  {cfg.nameLabel} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={cfg.namePlaceholder}
                  style={inputStyle}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none transition"
                  required
                />
              </div>

              <div>
                <label style={labelStyle} className="block text-sm font-medium text-gray-700 mb-2">
                  {cfg.phoneLabel} <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={cfg.phonePlaceholder}
                  style={inputStyle}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none transition"
                  required
                />
              </div>

              <div>
                <label style={labelStyle} className="block text-sm font-medium text-gray-700 mb-2">
                  {cfg.messageLabel} <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  placeholder={cfg.messagePlaceholder}
                  style={inputStyle}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none transition resize-none"
                  required
                />
              </div>

              {showPrimary && (
                <button
                  type="submit"
                  style={
                    primaryHasCustom
                      ? {
                          backgroundColor: style?.primaryBtnBg || undefined,
                          color: style?.primaryBtnText || undefined,
                        }
                      : undefined
                  }
                  className={`w-full font-bold py-4 px-8 rounded-lg text-lg transition-all duration-300 hover:shadow-lg flex items-center justify-center ${
                    primaryHasCustom ? "hover:opacity-90" : "bg-green-600 hover:bg-green-700 text-white"
                  }`}
                >
                  {primaryLabel}
                  <Send className="ml-2 w-5 h-5 mr-2" />
                </button>
              )}

              <p
                style={{ color: style?.ctaMutedTextColor || undefined }}
                className={`${headerAlignClass} text-sm text-gray-500 mt-6`}
              >
                {cfg.privacyText}
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}